import { useMemo } from 'react';

/**
 * Custom hook to transform Sequencing question data
 */
export function useSequencingData(question) {
  return useMemo(() => {
    let choices = [];
    
    if (question.choices && Array.isArray(question.choices)) {
      choices = question.choices.map((choice) => ({
        idx: choice.idx !== undefined ? choice.idx : choice.id,
        text: choice.text || choice['option-text'] || '',
      }));
    } else if (question.blocks && Array.isArray(question.blocks)) {
      choices = question.blocks.map((text, index) => ({
        idx: index,
        text: text || '',
      }));
    }

    return {
      ...question,
      choices: choices,
      text: question.text || question['text-soal'] || '',
    };
  }, [question]);
}

/**
 * Get choice text by idx
 */
export function getChoiceTextByIdx(choices, idx) {
  const choice = choices.find(c => {
    return c.idx === idx || Number(c.idx) === Number(idx);
  });
  return choice?.text || `[Unknown: ${idx}]`;
}

/**
 * Normalize user answer indices
 */
export function normalizeUserAnswerIndices(userAnswer) {
  if (!Array.isArray(userAnswer) || userAnswer.length === 0) {
    return [];
  }
  
  if (typeof userAnswer[0] === 'number') {
    return userAnswer.map(idx => Number(idx));
  }
  
  if (typeof userAnswer[0] === 'object' && userAnswer[0] !== null) {
    return userAnswer.map(item => {
      if (item.idx !== undefined) return Number(item.idx);
      if (item.id !== undefined) return Number(item.id);
      return null;
    }).filter(idx => idx !== null);
  }
  
  return [];
}

/**
 * Normalize correct answer indices (supports multiple sequences)
 */
export function normalizeCorrectAnswerIndices(correctAnswer) {
  if (!Array.isArray(correctAnswer) || correctAnswer.length === 0) {
    return [];
  }
  
  // Single sequence
  if (typeof correctAnswer[0] === 'number') {
    return [correctAnswer.map(idx => Number(idx))];
  }
  
  // Multiple sequences
  if (Array.isArray(correctAnswer[0])) {
    return correctAnswer
      .filter(arr => Array.isArray(arr) && arr.length > 0)
      .map(arr => {
        if (typeof arr[0] === 'number') {
          return arr.map(idx => Number(idx));
        }
        if (typeof arr[0] === 'object' && arr[0] !== null) {
          return arr.map(item => {
            if (item.idx !== undefined) return Number(item.idx);
            if (item.id !== undefined) return Number(item.id);
            return null;
          }).filter(idx => idx !== null);
        }
        return null;
      })
      .filter(arr => arr !== null && arr.length > 0);
  }
  
  return [];
}

/**
 * Convert indices to text array
 */
export function convertIndicesToText(indices, getChoiceTextByIdxFn) {
  if (indices.length > 0) {
    return indices.map(idx => getChoiceTextByIdxFn(idx));
  }
  return [];
}

